class CartAutoAdd {
  constructor() {
    this.addOnRules = this.getAddOnRules();
    this.cartUpdateUnsubscriber = undefined;
    this.init();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  init() {
    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });

    // Initial check on page load
    this.checkAddOnRules();
  }

  getAddOnRules() {
    // Look for cart drawer section or cart drawer elements
    const cartDrawerSection = document.querySelector('[data-section-type="cart-drawer"]') || 
                              document.querySelector('cart-drawer') ||
                              document.querySelector('#CartDrawer');
    
    if (!cartDrawerSection) {
      return [];
    }

    const addOnBlocks = cartDrawerSection.querySelectorAll('[data-block-type="add_ons"]');
    const totalThresholdBlocks = cartDrawerSection.querySelectorAll('[data-block-type="total_threshold"]');
        
    const rules = [];

    // Process add-on blocks
    addOnBlocks.forEach((block, index) => {
      const targetProductId = block.dataset.targetProductId;
      const payloadProductId = block.dataset.payloadProductId;
      const thresholdQuantity = parseInt(block.dataset.thresholdQuantity) || 1;
      const payloadQuantity = parseInt(block.dataset.payloadQuantity) || 1;
      const removeWhenBelowThreshold = block.dataset.removeWhenBelowThreshold === 'true';

      if (targetProductId && payloadProductId) {
        rules.push({
          type: 'add_on',
          targetProductId: parseInt(targetProductId),
          payloadProductId: parseInt(payloadProductId),
          thresholdQuantity,
          payloadQuantity,
          removeWhenBelowThreshold
        });
      }
    });

    // Process total threshold blocks
    totalThresholdBlocks.forEach((block, index) => {
      const thresholdAmount = parseFloat(block.dataset.thresholdAmount) || 0;
      const payloadProductId = block.dataset.payloadProductId;
      const payloadQuantity = parseInt(block.dataset.payloadQuantity) || 1;
      const removeWhenBelowThreshold = block.dataset.removeWhenBelowThreshold === 'true';
      const thresholdType = block.dataset.thresholdType || 'above';

      if (payloadProductId) {
        rules.push({
          type: 'total_threshold',
          thresholdAmount,
          payloadProductId: parseInt(payloadProductId),
          payloadQuantity,
          removeWhenBelowThreshold,
          thresholdType
        });
      }
    });

    return rules;
  }

  async handleCartUpdate(event) {
    // Small delay to ensure cart data is updated
    setTimeout(() => {
      this.checkAddOnRules();
    }, 100);
  }

  refreshRules() {
    this.addOnRules = this.getAddOnRules();
  }

  async checkAddOnRules() {
    if (this.addOnRules.length === 0) return;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cartData = await cartResponse.json();

      for (const rule of this.addOnRules) {
        await this.processAddOnRule(rule, cartData);
      }
    } catch (error) {
      console.error('Error checking add-on rules:', error);
    }
  }

  async processAddOnRule(rule, cartData) {
    if (rule.type === 'add_on') {
      await this.processAddOnRuleType(rule, cartData);
    } else if (rule.type === 'total_threshold') {
      await this.processTotalThresholdRule(rule, cartData);
    }
  }

  async processAddOnRuleType(rule, cartData) {
    const targetItem = cartData.items.find(item => item.variant_id == rule.targetProductId);
    const payloadItem = cartData.items.find(item => item.variant_id == rule.payloadProductId);
    const payloadItemIndex = cartData.items.findIndex(item => item.variant_id == rule.payloadProductId);

    const targetQuantity = targetItem ? targetItem.quantity : 0;
    const payloadQuantity = payloadItem ? payloadItem.quantity : 0;
    const payloadLine = payloadItem ? payloadItemIndex + 1 : null;

    // Check if we need to add the payload product
    if (targetQuantity >= rule.thresholdQuantity && payloadQuantity === 0) {
      await this.addPayloadProduct(rule);
    }
    // Check if we need to remove the payload product
    else if (targetQuantity < rule.thresholdQuantity && payloadQuantity > 0 && rule.removeWhenBelowThreshold) {
      await this.removePayloadProduct(rule, payloadLine);
    }
    // Check if we need to adjust payload quantity based on target quantity
    else if (targetQuantity >= rule.thresholdQuantity && payloadQuantity > 0) {
      const expectedPayloadQuantity = Math.floor(targetQuantity / rule.thresholdQuantity) * rule.payloadQuantity;
      if (payloadQuantity !== expectedPayloadQuantity) {
        await this.updatePayloadQuantity(rule, expectedPayloadQuantity, payloadLine);
      }
    }
  }

  async processTotalThresholdRule(rule, cartData) {

    
    const payloadItem = cartData.items.find(item => item.variant_id == rule.payloadProductId);
    const payloadItemIndex = cartData.items.findIndex(item => item.variant_id == rule.payloadProductId); 


    const payloadQuantity = payloadItem ? payloadItem.quantity : 0;
    const payloadLine = payloadItem ? payloadItemIndex + 1 : null;
    
    // Calculate cart subtotal (excluding the payload product to avoid circular logic)
    const cartSubtotal = cartData.items
      .filter(item => item.variant_id != rule.payloadProductId)
      .reduce((total, item) => total + (item.final_line_price / 100), 0);


    let shouldHavePayload = false;
    
    if (rule.thresholdType === 'above') {
      shouldHavePayload = cartSubtotal >= rule.thresholdAmount;
    } else if (rule.thresholdType === 'below') {
      shouldHavePayload = cartSubtotal < rule.thresholdAmount;
    }

    // Check if we need to add the payload product
    if (shouldHavePayload && payloadQuantity === 0) {
      await this.addPayloadProduct(rule);
    }
    // Check if we need to remove the payload product
    else if (!shouldHavePayload && payloadQuantity > 0 && rule.removeWhenBelowThreshold) {
      await this.removePayloadProduct(rule, payloadLine);
    }
    // Check if we need to adjust payload quantity
    else if (shouldHavePayload && payloadQuantity > 0 && payloadQuantity !== rule.payloadQuantity) {
      await this.updatePayloadQuantity(rule, rule.payloadQuantity, payloadLine);
    }
  }

  async addPayloadProduct(rule) {
    const body = JSON.stringify({
      items: [{
        id: rule.payloadProductId,
        quantity: rule.payloadQuantity
      }]
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.status) {
          console.error('Error adding payload product:', data);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-auto-add',
            productVariantId: rule.payloadProductId,
            errors: data.errors || data.description,
            message: data.message,
          });
          return;
        }

        console.log(`Auto-added payload product ${rule.payloadProductId}`);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-auto-add', cartData: data });
      })
      .catch((e) => {
        console.error('Error adding payload product:', e);
      });
  }

  async removePayloadProduct(rule, line) {
    if (!line) {
      console.log('Payload product not found in cart (already removed or never added)');
      return;
    }

    // Use existing cart infrastructure
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartItems) {
      cartItems.updateQuantity(line, 0, '', rule.payloadProductId);
      console.log(`Auto-removed payload product at line ${line}`);
    }
  }

  async updatePayloadQuantity(rule, newQuantity, line) {
    if (!line) {
      console.log('Payload product not found in cart (cannot update quantity)');
      return;
    }

    // Use existing cart infrastructure
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartItems) {
      cartItems.updateQuantity(line, newQuantity, '', rule.payloadProductId);
      console.log(`Auto-updated payload product at line ${line} quantity to ${newQuantity}`);
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize cart auto-add when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartAutoAdd = new CartAutoAdd();
});

// Re-initialize when sections are loaded (for cart drawer)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('cart-drawer') || event.target.querySelector('.cart-drawer')) {
    if (window.cartAutoAdd) {
      window.cartAutoAdd.refreshRules();
    } else {
      window.cartAutoAdd = new CartAutoAdd();
    }
  }
}); 